// 初始化状态
import React, { useState, useEffect } from "react"
import {
  TextInput,
  NativeSyntheticEvent,
  TextInputSelectionChangeEventData,
  Keyboard,
} from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import { useTheme } from "~/ui/styled"
import { BetweenFlex, ColumnFlex, Text, Button, IconFont, GradientButton } from "~components/El"
import styled from "~/ui/styled"
import { Chat } from "~models/chat"
import { useStores } from "~models"
import { MessageTypes, CardType, ChatTypes } from "~proto"
import MessageTextInput from "./message-text-input"
import { InputUI } from "./input-ui"
import { PhotosModelProps } from "./photos-mode"
import { useChatList } from "~hooks/useChatList"
import { globalToast } from "~utils"
import { useWebview } from '~components/WebviewContext';
import { useModal } from "~hooks/useModal";

interface Props {
  mode?: InputUI
  chat: Chat,
  content: string
  channelChatInfo?: any
  onVoice: () => void
  onCameraOpen: () => void
  onPhotos?: () => void
  inputRef: React.RefObject<TextInput>
  onKeyPress?: (key: string) => void
  onSelectionChange?: (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => void
  onChangeText: (t: string) => void
  onSubmit: () => void
  onEmoji: () => void
  onFile?: () => void
  onTransfer?: () => void
  onFocus?: () => void
  onBlur?: () => void
  autoFocus?: boolean
  selectedPhotos?: PhotosModelProps["selectedPhotos"]
  submitSelectedPhotos?: PhotosModelProps["submitSelectedPhotos"]
  setMode?: (string) => void
  children?: any // emoji 列表 or 图片预览
}

const PanelArea = styled.div`
  width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
`

const InputToolbar = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
`

const DefaultMode: React.FC<Props> = ({
  mode,
  chat,
  content,
  channelChatInfo,
  onVoice,
  onCameraOpen,
  onPhotos,
  inputRef,
  onKeyPress,
  onSelectionChange,
  onChangeText,
  onSubmit,
  onFocus,
  onBlur,
  onEmoji,
  onFile,
  onTransfer,
  autoFocus,
  children,
  selectedPhotos,
  submitSelectedPhotos,
  setMode,
}) => {
  const theme = useTheme()
  const { messageStore, userStore, chatStore, apiStore, walletStore } = useStores()
  const {
    openRedPacketForm,
    openTransferForm,
    openRevenueStatistics,
    openUpwardFunds,
    openDownwardFunds,
    openShareCard
  } = useChatList(chat);
  const { Dialog } = useModal()
  const { openWebview } = useWebview()
  const canSpeak = chat?.type !== ChatTypes.GROUP || chat?.isCurrentUserCanSpeak

  const guardSpeak = (fn?: () => void) => {
    if (!canSpeak) {
      globalToast.show("全群禁言中")
      return
    }
    fn?.()
  }
  // plus 展开
  const [showPlus, setShowPlus] = useState(false)

  // 键盘弹起时，如果 plus 展开则收起
  useEffect(() => {
    const sub = Keyboard.addListener("keyboardDidShow", () => {
      setShowPlus(false)
    })
    return () => sub.remove()
  }, [])

  return (
    <ColumnFlex width="100%" height="auto">
      <InputToolbar>
      {/* 输入区域 */}
      <BetweenFlex height="auto" width="100%" padding="8px 12px" gap="10px">
        <Button
          width="36px"
          height="36px"
          borderRadius="18px"
          bg={mode === InputUI.Voice ? theme.colors.primary : theme.colors.background}
          onClick={(e) => {
            e.stopPropagation()
            setShowPlus(false)
            if (mode !== InputUI.Voice) {
              Keyboard.dismiss()
            }
            onVoice()
          }}
        >
          <IconFont name={mode === InputUI.Voice ? "a-Frame1561999602" : "yy"} size={16} color={mode === InputUI.Voice ? theme.colors.blockBackground: theme.colors.secondary} />
        </Button>

        {/* 输入框 */}
        <BetweenFlex
          flex={1}
          height="auto"
          padding="6px 6px 6px 12px"
          borderRadius="18px"
          bg={theme.colors.background}
        >
          <MessageTextInput
            inputRef={inputRef}
            onKeyPress={onKeyPress}
            onSelectionChange={onSelectionChange}
            onChangeText={onChangeText}
            content={content}
            onSubmit={onSubmit}
            onFocus={() => {
              setShowPlus(false)
              onFocus?.()
            }}
            onBlur={onBlur}
            autoFocus={autoFocus}
            editable={!(mode === InputUI.Voice)}
            placeholder="请输入内容…"
          />

          {/* emoji 按钮 */}
          <Button
            onClick={() => {
              setShowPlus(false)
              Keyboard.dismiss()
              onEmoji()
            }}
          >
            <IconFont name={"Union-1"} size={24} color={theme.colors.secondary} />
          </Button>
        </BetweenFlex>

        {/* + 按钮 */}
        <Button
          width="36px"
          height="36px"
          borderRadius="18px"
          bg={theme.colors.background}
          onClick={() => {
            Keyboard.dismiss()
            if (showPlus) {
              setShowPlus(false)
            } else {
              setMode?.(InputUI.Default)
              setShowPlus(true)
            }
          }}
        >
          <IconFont name={"Union-2"} size={16} color={theme.colors.secondary} />
        </Button>

        {/* 发送 */}
        <Button width="36px" height="36px" borderRadius="18px" onClick={() => {
          const photos = selectedPhotos ?? []
          if (photos.length > 0 || mode === InputUI.Photos) {
            submitSelectedPhotos?.(photos)
          } else {
            onSubmit()
          }
        }}>
          <IconFont name={"fs"} size={30} color={theme.colors.primary} />
        </Button>
      </BetweenFlex>
      </InputToolbar>

      {/* plus 展开区域 */}
      {showPlus ? (
        <ColumnFlex width="100%" padding="0 12px">
            {/* 上排 */}
            <BetweenFlex width="100%" gap="12px">
              <Button
                flex={1}
                flexDirection="column"
                height="60px"
                borderRadius={theme.radii.lg}
                bg={theme.colors.background}
                onClick={() => guardSpeak(() => {
                  setShowPlus(false)
                  onPhotos?.()
                })}
              >
                <IconFont name={"zhaopian"} size={20} color={theme.colors.primaryText} />
                <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md} mt="5px">
                  图片
                </Text>
              </Button>
              <Button
                flex={1}
                flexDirection="column"
                height="60px"
                borderRadius={theme.radii.lg}
                bg={theme.colors.background}
                onClick={() => guardSpeak(onCameraOpen)}
              >
                <IconFont name={"pz"} size={20} color={theme.colors.primaryText} />
                <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md} mt="5px">
                  拍照
                </Text>
              </Button>
              <Button
                flex={1}
                flexDirection="column"
                height="60px"
                borderRadius={theme.radii.lg}
                bg={theme.colors.background}
                onClick={() => {
                  guardSpeak(() =>
                    openShareCard({
                      title: "选择要分享的联系人",
                      confirmText: "发送",
                      onConfirm: (contacts = []) => {
                        let senderId = userStore?.currentUser?.id // 发送用户的id
                        let shareUserId = chat.id // 分享用户的id
                        let shareChatId = chat.chat_id // 分享聊天的id
                        let content = chat.title || chat.displayTitle || "" // 分享内容
                        if (chat?.chatTypeEnum === "PRIVATE") {
                          senderId = chat.receiver?.id
                          shareUserId = chat.receiver?.id
                          content = chat.receiver?.name || ""
                        }
                        if (!senderId) return
                        
                        contacts.forEach((contact) => {
                          const targetId = contact?.user_id
                          if (!targetId) return
                          apiStore.getChatRoomId({ username: contact?.user?.username }).then((res) => {
                            if (res?.data?.id && res?.data?.chat_id) {
                              const mChat = chatStore.getChat(res?.data?.chat_id)
                              const m = mChat.newMessage({
                                type: MessageTypes.SHARE_CARD,
                                content: content,
                                attach: {
                                  card_info: {
                                    id: shareUserId,
                                    chat_cid: shareChatId,
                                    type: CardType[chat?.chatTypeEnum],
                                    sender_id: senderId,
                                  },
                                },
                              })
                              messageStore.sendMessageByMessageType(m)
                            } else {
                              const newChat = chatStore.newPrivateChat({
                                receiver_id: targetId,
                                name: contact?.user?.name || contact?.displayName,
                              })
                              const m = newChat.newMessage({
                                type: MessageTypes.SHARE_CARD,
                                content: content,
                                attach: {
                                  card_info: {
                                    id: shareUserId,
                                    chat_cid: shareChatId,
                                    type: CardType[chat?.chatTypeEnum],
                                    sender_id: senderId,
                                  },
                                },
                              })
                              messageStore.sendMessageByMessageType(m)
                            }
                          })
                          
                        })
                      },
                    }),
                  )
                }}
              >
                <IconFont name={"Frame"} size={20} color={theme.colors.primaryText} />
                <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.md} mt="5px">
                  名片
                </Text>
              </Button>
            </BetweenFlex>

            {/* 下排 */}
            <BetweenFlex width="100%" gap="12px" mt="12px">
              {/* 私聊和群聊有红包和转账 */}
              {["GROUP", "PRIVATE"].includes(chat?.chatTypeEnum) && (
                <>
                  <GradientButton
                    flex={1}
                    height="60px"
                    colors={['#fb994d', '#ee4b49']}
                    onClick={() => {
                      if (!walletStore.wp_confirmed) {
                        return Dialog.show({
                            title: "设置支付密码",
                            content: "您尚未设置支付密码，请先设置支付密码",
                            confirmText: "去设置",
                            onConfirm: () => {
                                openWebview({ path: "/user/set-wp" })
                            }
                        })
                      } else {
                        guardSpeak(() => openRedPacketForm({ channelChatInfo, onSuccess: () => setShowPlus(false) }))
                      }
                    }}
                  >
                    <IconFont name={"Frame-1"} size={20} color={theme.colors.textLight} />
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.md} mt="5px">
                      红包
                    </Text>
                  </GradientButton>

                  <GradientButton
                    flex={1}
                    height="60px"
                    colors={['#74b0fa', '#4e98f3']}
                    onClick={() => {
                      if (!walletStore.wp_confirmed) {
                        return Dialog.show({
                            title: "设置支付密码",
                            content: "您尚未设置支付密码，请先设置支付密码",
                            confirmText: "去设置",
                            onConfirm: () => {
                                openWebview({ path: "/user/set-wp" })
                            }
                        })
                      } else {
                        guardSpeak(() => openTransferForm({channelChatInfo}))
                      }
                    }}
                  >
                    <IconFont name={"qlzz"} size={20} color={theme.colors.textLight} />
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.md} mt="5px">
                      {chat?.chatTypeEnum === "GROUP" ? "群聊" : ""}转账
                    </Text>
                  </GradientButton>
                </>
              )}
              {/* 频道有上压资金和下压资金 */}
              {chat?.chatTypeEnum === "CHANNEL" && (
                <>
                  <GradientButton
                    flex={1}
                    height="60px"
                    colors={['#fb994d', '#ee4b49']}
                    onClick={() => {
                      setShowPlus(false)
                      openUpwardFunds({
                        channelChatInfo,
                        onSuccess: () => setShowPlus(false),
                      })
                    }}
                  >
                    <IconFont name={"jiahao"} size={20} color={theme.colors.textLight} />
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.md} mt="5px">
                      上压资金
                    </Text>
                  </GradientButton>

                  <GradientButton
                    flex={1}
                    height="60px"
                    colors={['#74b0fa', '#4e98f3']}
                    onClick={() => {
                      setShowPlus(false)
                      openDownwardFunds({ channelChatInfo })
                    }}
                  >
                    <IconFont name={"cbxs"} size={20} color={theme.colors.textLight} />
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.md} mt="5px">
                      下压资金
                    </Text>
                  </GradientButton>
                </>
              )}
              
              {chat?.isGameGroup && (
                <GradientButton
                  flex={1}
                  height="60px"
                  colors={['#07c160', '#32df85']}
                  onClick={() => openRevenueStatistics(chat)}
                >
                  <IconFont name={"jctj"} size={20} color={theme.colors.textLight} />
                  <Text color={theme.colors.textLight} fontSize={theme.fontSizes.md} mt="5px">
                    收益统计
                  </Text>
                </GradientButton>
              )}
              
            </BetweenFlex>
          </ColumnFlex>
      ) : null}

      {/* emoji / 图片 / 语音等面板 */}
      {children ? <PanelArea>{children}</PanelArea> : null}
      
    </ColumnFlex>
  )
}

export default observer(DefaultMode)
